import React from 'react';
import PropTypes from 'prop-types';
import { Header } from 'semantic-ui-react';
import _get from 'lodash/get';

import HoursOfOperationDaily from './HoursOfOperationDaily';
import HoursOfOperationDailyWithPopup from './HoursOfOperationWithPopup';
import HoursOfOperationModal from './HoursOfOperationModal';
import HoursOfOperationLabeled from './HoursOfOperationLabeled';
import IconElement from '../IconElement';
import {
  groupByDay,
  groupByDayAndTime,
  groupByLabel,
  groupByDayLabel,
  setTodayFirstInArray,
  isTimeDefined,
  NO_LABEL,
  HOURS_LABELS,
  getHourLabel,
} from './utils';
import './HoursOfOperation.css';

export const isHoursAvailable = hours => {
  return hours.filter(hour => isTimeDefined(hour.open) || isTimeDefined(hour.close)).length > 0;
};

export default class HoursOfOperation extends React.Component {
  constructor(props) {
    super(props);
    this.createLabelForDays = this.createLabelForDays.bind(this);
  }

  getLabeledComponentAndFirstDay() {
    const {
      firstGroupLevel,
      hours,
      displayOption,
      startWithToday,
      timezone,
      orientation,
      animated,
      celled,
      inlineHours,
    } = this.props;

    const isHorizontal = orientation === 'horizontal';
    let data = null;
    let firstDay = null;

    if (firstGroupLevel === 'label') {
      data = groupByLabel(hours, this.createLabelForDays, displayOption, startWithToday, timezone);
      firstDay = _get(data, '[0][1][0]');
      firstDay.mainLabel = _get(data, '[0][0]');
    } else {
      data = groupByDayLabel(
        hours,
        this.createLabelForDays,
        displayOption,
        startWithToday,
        timezone,
      );
      firstDay = _get(data, '[0][1][0]');
      firstDay.dayLabel = _get(data, '[0][0]');
    }

    const component = (
      <HoursOfOperationLabeled
        data={data}
        isHorizontal={isHorizontal}
        animated={animated}
        celled={celled}
        inlineHours={inlineHours}
      />
    );

    return [component, firstDay];
  }

  getDailyComponentAndFirstDay() {
    const {
      hours,
      displayOption,
      startWithToday,
      timezone,
      orientation,
      animated,
      celled,
      inlineHours,
    } = this.props;

    const isHorizontal = orientation === 'horizontal';

    let hourGroups = null;
    if (displayOption === 'grouped') {
      hourGroups = groupByDayAndTime(hours, this.createLabelForDays);
    } else {
      hourGroups = groupByDay(hours, this.createLabelForDays);
    }

    const startTodayHourGroups = setTodayFirstInArray(hourGroups, timezone);
    hourGroups = startWithToday ? startTodayHourGroups : hourGroups;

    const component = (
      <HoursOfOperationDaily
        hourGroups={hourGroups}
        isHorizontal={isHorizontal}
        animated={animated}
        celled={celled}
        inlineHours={inlineHours}
      />
    );
    const firstDay = startTodayHourGroups[0];

    return [component, firstDay];
  }

  getPopUpLabelForLabeled(hourDay) {
    const { firstGroupLevel } = this.props;

    let label = '';
    let secondaryLabel = '';
    if (firstGroupLevel === 'day') {
      [label, secondaryLabel] = HoursOfOperation.getPopUpLabelForDaily(hourDay);
    }
    if (firstGroupLevel === 'label') {
      label = hourDay.mainLabel === NO_LABEL ? 'See hours' : hourDay.mainLabel;
      secondaryLabel = hourDay.hoursValue.join(' | ');
    }

    return [label, secondaryLabel];
  }

  static getPopUpLabelForDaily(hourDay) {
    let label = '';
    let secondaryLabel = '';
    if (!hourDay) {
      label = 'See hours';
    } else {
      label = hourDay.dayLabel;
      secondaryLabel = hourDay.hoursValue.join(' | ');
    }

    return [label, secondaryLabel];
  }

  formatLabel(label) {
    const { capitalizeLabels, truncateLabels } = this.props;
    const displayedLabel = truncateLabels ? label.slice(0, 3) : label;
    return capitalizeLabels ? displayedLabel.toUpperCase() : displayedLabel;
  }

  createLabelForDays(firstDay, secondDay = null) {
    const firstLabel = this.formatLabel(HOURS_LABELS[firstDay]);

    if (secondDay !== null) {
      const secondLabel = this.formatLabel(HOURS_LABELS[secondDay]);
      return `${firstLabel} - ${secondLabel}`;
    }

    return firstLabel;
  }

  render() {
    const {
      className,
      showIcon,
      displayOption,
      iconAs,
      hours,
      timezone,
      displayOpenClosedStatus,
      header,
      subheader,
    } = this.props;

    const joinedClassName = ['hours-of-operation', className].join(' ').trim();

    if (!isHoursAvailable(hours)) {
      return null;
    }

    const hoursHaveLabels = hours.some(hour => !!getHourLabel(hour));

    let mainComponent = null;
    let firstDay = null;
    let mainPopupLabel = null;
    let secondaryPopupLabel = null;

    if (hoursHaveLabels) {
      [mainComponent, firstDay] = this.getLabeledComponentAndFirstDay();
      [mainPopupLabel, secondaryPopupLabel] = this.getPopUpLabelForLabeled(firstDay);
    } else {
      [mainComponent, firstDay] = this.getDailyComponentAndFirstDay();
      [mainPopupLabel, secondaryPopupLabel] = HoursOfOperation.getPopUpLabelForDaily(firstDay);
    }

    if (displayOption === 'dailyWithPopup') {
      return (
        <HoursOfOperationDailyWithPopup
          displayOpenClosedStatus={displayOpenClosedStatus}
          timezone={timezone}
          hours={firstDay.hours}
          label={mainPopupLabel}
          secondaryLabel={secondaryPopupLabel}
        >
          {mainComponent}
        </HoursOfOperationDailyWithPopup>
      );
    }

    const mainComponentWithHeaders = (
      <div className={joinedClassName}>
        {header && <Header as="h2">{header}</Header>}
        {subheader && <Header as="h3">{subheader}</Header>}
        {mainComponent}
      </div>
    );

    if (displayOption === 'modal') {
      return (
        <HoursOfOperationModal iconAs={iconAs}>{mainComponentWithHeaders}</HoursOfOperationModal>
      );
    }

    return showIcon ? (
      <IconElement iconType="clock" element={mainComponentWithHeaders} iconPosition="top" />
    ) : (
      mainComponentWithHeaders
    );
  }
}

HoursOfOperation.propTypes = {
  hours: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.number.isRequired,
      open: PropTypes.number.isRequired,
      close: PropTypes.number.isRequired,
      label: PropTypes.string,
    }),
  ).isRequired,
  animated: PropTypes.bool,
  capitalizeLabels: PropTypes.bool,
  celled: PropTypes.bool,
  className: PropTypes.string,
  displayOption: PropTypes.oneOf(['daily', 'dailyWithPopup', 'grouped', 'modal']),
  displayOpenClosedStatus: PropTypes.bool,
  startWithToday: PropTypes.bool,
  header: PropTypes.string,
  inlineHours: PropTypes.bool,
  iconAs: PropTypes.string,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  showIcon: PropTypes.bool,
  subheader: PropTypes.string,
  timezone: PropTypes.string,
  truncateLabels: PropTypes.bool,
  firstGroupLevel: PropTypes.oneOf(['label', 'day']),
};

HoursOfOperation.defaultProps = {
  animated: false,
  capitalizeLabels: false,
  celled: false,
  className: '',
  displayOption: 'daily',
  displayOpenClosedStatus: false,
  startWithToday: false,
  header: '',
  inlineHours: false,
  iconAs: 'icon',
  orientation: 'vertical',
  showIcon: false,
  subheader: '',
  timezone: null,
  truncateLabels: false,
  firstGroupLevel: 'day',
};

HoursOfOperation.propOptions = {
  header: ['Hours of Operation', 'Our Hours', ''],
};

HoursOfOperation.dataPropsMap = {
  hours: ['business', 'primary_location', 'hours'],
};
